/* global SERVER_BASE_URL, ZENDESK_HELP_CENTER_URL */
import React, { Component } from 'react'
import q from 'query-string'
import { notification } from 'antd'
import { DEFAULT_ROUTE } from '@constants/routes'
import { getToken } from '@auth/token-service'
import { isSafeExternalUrl, isSafeInternalPath } from '@helpers/urlSafety'

// The only origins we will append an access token to.
const PYLON_CALLBACK_ORIGINS = ['https://graph.usepylon.com']

class SSOLogin extends Component {
  // /sso-login is reached either as a direct redirect from Zendesk's help
  // center (Zendesk flow) or from the Help Center life-ring appending
  // ?provider=pylon (Pylon flow) — there is no other in-app navigation to this
  // route — so the SSO flow must kick off on initial mount. Previously this
  // lived in componentDidUpdate and only worked because withLDConsumer() forced
  // a re-render once LaunchDarkly flags arrived. PLAT-2687 removed the LD
  // wrapper, which removed the only thing triggering a re-render, which
  // silently broke SSO. componentDidMount makes the dependency explicit — and
  // the Pylon vs Zendesk choice comes from the URL (set where LD is loaded), so
  // it stays independent of LaunchDarkly timing here.
  componentDidMount() {
    const parsed_url = q.parse(this.props.location.search)
    if (parsed_url.provider === 'pylon') {
      this.proceedPylonSso()
      return
    }
    this.proceedZendeskSso(parsed_url)
  }

  proceedPylonSso = async () => {
    // Two flows reach here, distinguished by whether Pylon handed us its own redirect_uri:
    //  - Pylon-initiated (inbound, BOTS-51): Pylon bounces an unauthenticated portal/KB
    //    visitor to our Login URL with ?redirect_uri=<Pylon graph callback>. We authenticate
    //    them here, mint the JWT, and send them back to that redirect_uri with access_token
    //    appended so Pylon lands them where they started, signed in.
    //  - Platform-initiated (in-app life-ring): no redirect_uri — follow the BFF's pre-built
    //    redirect (JWT + org slug + kbUrl → KB landing).
    const redirectUri = q.parse(this.props.location.search).redirect_uri
    try {
      const response = await fetch(SERVER_BASE_URL + '/api/sso/pylon', {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'content-type': 'application/json',
        },
      })
      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error)
      }
      if (redirectUri) {
        // Query-string supplied, and the access token is appended below, so the
        // origin must be pinned — a scheme check alone would leak the token.
        if (!isSafeExternalUrl(redirectUri, PYLON_CALLBACK_ORIGINS)) {
          throw new Error('SSO Failed: Untrusted redirect URL')
        }
        const separator = redirectUri.includes('?') ? '&' : '?'
        window.location.assign(`${redirectUri}${separator}access_token=${data.access_token}`)
      } else {
        // BFF returns the full platform-initiated redirect (JWT + org slug).
        // Validated so a tampered response cannot make this a javascript: sink.
        if (!isSafeExternalUrl(data.redirect_url)) {
          throw new Error('SSO Failed: Untrusted redirect URL')
        }
        window.location.assign(data.redirect_url)
      }
    } catch (error) {
      notification.error({ message: error.message })
      this.props.history.push(`/${DEFAULT_ROUTE}`)
    }
  }

  proceedZendeskSso = async (parsed_url) => {
    try {
      const brand_id = parsed_url.brand_id
      const locale_id = parsed_url.locale_id
      const return_to = parsed_url.return_to
      if (!brand_id || !locale_id || !return_to) {
        throw new Error('SSO Failed: Missing required parameters')
      }
      // return_to comes from the query string and is handed to Zendesk with a
      // freshly minted JWT. Zendesk sends a full URL or a path relative to its
      // own help center, so allow both and reject anything else.
      if (!isSafeExternalUrl(return_to) && !isSafeInternalPath(return_to)) {
        throw new Error('SSO Failed: Untrusted return_to URL')
      }
      const response = await fetch(SERVER_BASE_URL + '/api/sso/zendesk-jwt', {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'content-type': 'application/json',
        },
      })
      const data = await response.json()
      if (response.ok) {
        this.submitSsoJwtForm(data.jwt, return_to)
      } else {
        throw new Error(data.error)
      }
    } catch (error) {
      notification.error({ message: error.message })
      this.props.history.push(`/${DEFAULT_ROUTE}`)
    }
  }

  submitSsoJwtForm = (jwt, returnTo) => {
    const ssoForm = document.createElement('form')
    ssoForm.method = 'POST'
    ssoForm.action = `${ZENDESK_HELP_CENTER_URL}/access/jwt?return_to=${encodeURIComponent(returnTo)}`

    const jwtInput = document.createElement('input')
    jwtInput.type = 'hidden'
    jwtInput.name = 'jwt'
    jwtInput.value = jwt
    ssoForm.appendChild(jwtInput)

    document.body.appendChild(ssoForm)
    ssoForm.submit()
  }

  render() {
    return <div></div>
  }
}

export default SSOLogin
